'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { EventSearch } from '@/components/shared/EventSearch';
import { TimeFilter, TimeFilterOption } from '@/components/shared/TimeFilter';
import { SeparatedEvents } from '@/components/events/SeparatedEvents';
import { SignedIn } from '@clerk/nextjs';
import { useEvents, Event } from '@/lib/hooks/useEvents';
import { categories } from '@/lib/constants';

export default function Events() {
	const [selectedCategory, setSelectedCategory] = useState<string>('trending');
	const [timeFilter, setTimeFilter] = useState<TimeFilterOption>('this-week');
	const { events, loading, error } = useEvents(true, timeFilter);
	const router = useRouter();
	const [isStandalone, setIsStandalone] = React.useState(false);

	React.useEffect(() => {
		setIsStandalone(window.matchMedia('(display-mode: standalone)').matches);
	}, []);

	// Filter events based on the selected category
	const filteredEvents =
		selectedCategory === 'trending'
			? events
			: events.filter((event) => event.category === selectedCategory);

	return (
		<main className='w-screen h-full flex flex-col gap-6 px-4 mb-48'>
			{/* Header Section */}
			<section className='flex flex-col gap-4 mt-3' id='explore-header'>
				<div className='w-full flex flex-col md:flex-row gap-4'>
					<EventSearch />
					<SignedIn>
						<Button
							className='w-1/2 md:w-1/4 bg-background text-foreground text-sm md:text-lg border-foreground rounded-full border-2 hover:bg-foreground hover:text-background transition-all duration-300'
							asChild
						>
							<Link href='/events/add-event'>Add An Event</Link>
						</Button>
					</SignedIn>
				</div>

				{/* Time Filter */}
				<TimeFilter
					selectedFilter={timeFilter}
					onFilterChange={setTimeFilter}
				/>
			</section>

			{/* Category Filters */}
			<section className='w-full'>
				<h3 className='text-lg font-semibold text-foreground mb-4'>
					Categories
				</h3>
				<ul className='w-full flex flex-row gap-4 overflow-x-auto pb-2 no-scrollbar'>
					{categories.map((category) => (
						<li
							key={category.id}
							className='flex flex-col gap-2 items-center min-w-fit'
						>
							<Button
								onClick={() => setSelectedCategory(category.id)}
								className={`h-16 w-16 rounded-full border-2 transition-all duration-300 ${
									selectedCategory === category.id
										? 'bg-foreground text-background'
										: 'bg-background text-foreground hover:bg-foreground hover:text-background'
								}`}
							>
								<category.icon className='w-8 h-8' />
							</Button>
							<p
								className={`text-sm text-center whitespace-nowrap ${
									selectedCategory === category.id
										? 'text-primary font-bold'
										: 'text-foreground'
								}`}
							>
								{category.name}
							</p>
						</li>
					))}
				</ul>
			</section>

			{/* Events List */}
			<section className='w-full'>
				<SeparatedEvents
					events={filteredEvents}
					loading={loading}
					error={error}
					isStandalone={isStandalone}
				/>
			</section>
		</main>
	);
}
